import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface Project {
  id: string;
  title: string;
  description?: string;
  descriptionKey?: string;
  image: string;
  link?: string;
  category: 'narrative' | 'commercial' | 'documentary' | 'music-video' | 'reel';
}

interface ProjectGalleryProps {
  projects: Project[];
  showAll?: boolean;
}

export default function ProjectGallery({ projects, showAll = true }: ProjectGalleryProps) {
  const { t } = useTranslation();

  // Add safety check for projects prop AFTER hooks
  if (!projects || !Array.isArray(projects)) {
    return <div className="w-full text-center py-8">No projects available</div>;
  }

  const displayProjects = showAll ? projects : projects.slice(0, 8);

  return (
    <div className="w-full">
      {displayProjects.map((project) => (
        <div key={project.id} className="group cursor-pointer w-full">
          {project.link ? (
            // Check if it's an internal video page link or external link
            project.link.startsWith('/video') ? (
              <Link to={project.link} className="block w-full">
                <ProjectItem project={project} />
              </Link>
            ) : (
              <a 
                href={project.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full"
              >
                <ProjectItem project={project} />
              </a>
            )
          ) : (
            <ProjectItem project={project} />
          )}
        </div>
      ))}
    </div>
  );
}

function ProjectItem({ project }: { project: Project }) {
  const { t } = useTranslation();
  
  // Check if the title is a translation key, if so translate it, otherwise use as is
  const getDisplayTitle = (title: string) => {
    if (title === 'evidenciasDoAmor' || title === 'benzinho' || title === 'aWolfAmongSwans' || title === 'techBrandCampaign' || title === 'osUltimosDiasDeGilda') {
      return t(title);
    }
    return title;
  };
  
  const displayTitle = getDisplayTitle(project.title);
  
  // Get description - use translated description if descriptionKey exists, otherwise use description
  const displayDescription = project.descriptionKey ? t(project.descriptionKey) : project.description;
  
  return (
    <div className="relative w-full overflow-hidden">
      <img
        src={project.image}
        alt={project.title}
        className="w-full h-auto object-cover object-top transition-transform duration-500 group-hover:scale-105"
        loading="lazy"
      />
      
      {/* Overlay with project info */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
        <h3 className="text-white font-medium text-lg mb-1">
          {displayTitle}
        </h3>
        {displayDescription && (
          <p className="text-white/80 text-sm leading-relaxed">
            {displayDescription}
          </p>
        )}
      </div>
    </div>
  );
}